import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { type StripeEnv, createStripeClient, getStripeErrorMessage } from "@/lib/stripe.server";
import { computeVenueTotalPence, bartenderPayPerHour, PLATFORM_FEE_PENCE, tieredPlatformFeePence, nextPlatformFeeTier } from "@/lib/pay-rates";
import { effectiveBillableHours } from "@/lib/break-utils";
import { computeCancellationOutcome } from "@/lib/cancellation-policy";
import { creditAppliedPence } from "@/lib/venue-credits";



type Result<T> = T | { error: string };

function hoursBetween(startIso: string, endIso: string): number {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return ms / 3_600_000;
}

// Venue starts a checkout session for an accepted-but-unpaid shift.
// Total = hourly_rate * hours * positions_needed.
export const createShiftCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      shift_id: z.string().uuid(),
      environment: z.enum(["sandbox", "live"]),
      return_url: z.string().url(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ clientSecret: string }>> => {
    try {
      const { supabase, userId } = context;
      const { data: shift, error } = await supabase
        .from("shifts")
        .select("id, venue_id, start_time, end_time, hourly_rate, status, payment_status, positions_needed, break_is_paid, pricing_model, platform_fee_pence")
        .eq("id", data.shift_id)
        .maybeSingle();
      if (error || !shift) throw new Error("Engagement not found.");
      if (shift.venue_id !== userId) throw new Error("Only the host venue may pay for this engagement.");
      // Allow payment once at least one bartender is accepted (checked below),
      // including partially-filled listings still marked "open".
      if (!["awaiting_payment", "open"].includes(shift.status as string)) {
        throw new Error("This engagement is not awaiting payment.");
      }
      if (shift.payment_status === "paid") throw new Error("This engagement is already paid.");

      const { data: acceptedBookings, error: bErr } = await supabase
        .from("bookings")
        .select("id, bartender_id")
        .eq("shift_id", shift.id)
        .eq("status", "accepted");
      if (bErr) throw new Error(bErr.message);
      if (!acceptedBookings || acceptedBookings.length === 0) throw new Error("No bartenders confirmed for this engagement.");

      const hours = hoursBetween(shift.start_time as string, shift.end_time as string);
      const billableHours = effectiveBillableHours(hours, (shift as { break_is_paid?: boolean }).break_is_paid);
      // requested_workers = positions_needed at posting time. The tier locks here.
      const requestedWorkers = (shift.positions_needed as number) ?? acceptedBookings.length;
      const pricingModel = (shift as { pricing_model?: "flat_fee_v1" | "legacy_margin" }).pricing_model ?? "flat_fee_v1";
      const sh = shift as { locked_platform_fee_pence?: number | null; platform_fee_pence?: number | null };
      const lockedFeePerWorker = pricingModel === "flat_fee_v1"
        ? (sh.locked_platform_fee_pence ?? sh.platform_fee_pence ?? tieredPlatformFeePence(requestedWorkers))
        : 0;

      // Persist the lock so under-fills retain this rate even if the tier
      // helper is later re-tuned.
      if (pricingModel === "flat_fee_v1" && sh.locked_platform_fee_pence == null) {
        await supabase.from("shifts")
          .update({ locked_platform_fee_pence: lockedFeePerWorker })
          .eq("id", shift.id);
      }

      // Pre-authorize the MAXIMUM possible cost (all requested workers fill).
      // Stripe partial-capture later releases the difference automatically.
      const wagePerWorkerPence = Math.round(Number(shift.hourly_rate) * billableHours * 100);
      const maxWagePence = wagePerWorkerPence * requestedWorkers;
      const maxFeePence = lockedFeePerWorker * requestedWorkers;
      const totalPence = maxWagePence + maxFeePence;
      if (totalPence < 100) throw new Error("Engagement amount is too low to charge.");

      // Auto-apply ONE £5 booking credit (e.g. from a bartender no-show), so
      // the venue's credits count down one per booking.
      let appliedCreditPence = 0;
      let stripeDiscounts: Array<{ coupon: string }> | undefined;
      try {
        const { data: prof } = await supabase
          .from("profiles")
          .select("credit_pence")
          .eq("id", userId)
          .maybeSingle();
        const available = Math.max(0, Number((prof as { credit_pence?: number } | null)?.credit_pence ?? 0));
        appliedCreditPence = creditAppliedPence(available, totalPence);

        if (appliedCreditPence > 0) {
          const stripeForCoupon = createStripeClient(data.environment as StripeEnv);
          const coupon = await stripeForCoupon.coupons.create({
            amount_off: appliedCreditPence,
            currency: "gbp",
            duration: "once",
            name: "Booking credit",
            metadata: { shift_id: shift.id, venue_id: userId, kind: "venue_credit" },
          });
          stripeDiscounts = [{ coupon: coupon.id }];
        }
      } catch (e) {
        console.error("credit apply skipped", e);
        appliedCreditPence = 0;
        stripeDiscounts = undefined;
      }

      const stripe = createStripeClient(data.environment as StripeEnv);

      const lineItems: Array<{
        price_data: { currency: string; product_data: { name: string }; unit_amount: number };
        quantity: number;
      }> = [
        {
          price_data: {
            currency: "gbp",
            product_data: {
              name: `Bartender wage — ${new Date(shift.start_time as string).toLocaleDateString("en-GB")} · up to ${requestedWorkers} staff (pre-auth)`,
            },
            unit_amount: wagePerWorkerPence,
          },
          quantity: requestedWorkers,
        },
      ];
      if (lockedFeePerWorker > 0) {
        lineItems.push({
          price_data: {
            currency: "gbp",
            product_data: { name: `Platform fee — £${(lockedFeePerWorker / 100).toFixed(2)} per bartender` },
            unit_amount: lockedFeePerWorker,
          },
          quantity: requestedWorkers,
        });
      }

      const session = await stripe.checkout.sessions.create({
        line_items: lineItems,
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.return_url,
        ...(stripeDiscounts ? { discounts: stripeDiscounts } : {}),
        payment_intent_data: {
          description: `Reserve engagement ${shift.id} (pre-auth, partial capture on completion)`,
          capture_method: "manual",
          metadata: {
            shift_id: shift.id, venue_id: userId, kind: "base",
            requested_workers: String(requestedWorkers),
            locked_fee_pence: String(lockedFeePerWorker),
            wage_per_worker_pence: String(wagePerWorkerPence),
            applied_credit_pence: String(appliedCreditPence),
          },
        },
        metadata: { shift_id: shift.id, venue_id: userId, kind: "base", applied_credit_pence: String(appliedCreditPence) },
      });

      await supabase.from("shifts").update({
        stripe_checkout_session_id: session.id,
        applied_credit_pence: appliedCreditPence,
      }).eq("id", shift.id);

      // Reserve the credit now so it can't double-spend across concurrent checkouts.
      if (appliedCreditPence > 0) {
        try {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          const { data: prof2 } = await supabaseAdmin
            .from("profiles").select("credit_pence").eq("id", userId).maybeSingle();
          const cur = Number((prof2 as { credit_pence?: number } | null)?.credit_pence ?? 0);
          const next = Math.max(0, cur - appliedCreditPence);
          await supabaseAdmin.from("profiles").update({ credit_pence: next }).eq("id", userId);
          await supabaseAdmin.from("venue_credit_ledger").insert({
            venue_id: userId,
            delta_pence: -appliedCreditPence,
            balance_after_pence: next,
            reason: "Applied to checkout",
            shift_id: shift.id,
          });
        } catch (e) { console.error("credit reservation write failed", e); }
      }

      return { clientSecret: session.client_secret ?? "" };
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });


// Checkout quote — drives the volume-tiered pricing breakdown shown on the
// venue's checkout screen, including a "request N more to unlock £X" hint.
export const getShiftCheckoutQuote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    shift_id: z.string().uuid(),
    actual_workers: z.number().int().min(0).max(500).optional(),
  }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: shift, error } = await supabaseAdmin
      .from("shifts")
      .select("id, venue_id, start_time, end_time, hourly_rate, positions_needed, break_is_paid, pricing_model, locked_platform_fee_pence, platform_fee_pence, captured_amount_pence, capture_status, last_captured_at")
      .eq("id", data.shift_id)
      .maybeSingle();
    if (error || !shift) throw new Error("Engagement not found.");
    if (shift.venue_id !== userId) throw new Error("Forbidden.");
    const hours = hoursBetween(shift.start_time as string, shift.end_time as string);
    const billableHours = effectiveBillableHours(hours, (shift as { break_is_paid?: boolean }).break_is_paid);
    const requestedWorkers = (shift.positions_needed as number) ?? 1;
    const pricingModel = (shift as { pricing_model?: "flat_fee_v1" | "legacy_margin" }).pricing_model ?? "flat_fee_v1";
    const sh = shift as { locked_platform_fee_pence?: number | null; platform_fee_pence?: number | null; captured_amount_pence?: number | null; capture_status?: string | null; last_captured_at?: string | null };
    const lockedFeePerWorker = pricingModel === "flat_fee_v1"
      ? (sh.locked_platform_fee_pence ?? sh.platform_fee_pence ?? tieredPlatformFeePence(requestedWorkers))
      : 0;
    const wagePerWorkerPence = Math.round(Number(shift.hourly_rate) * billableHours * 100);
    const wagePence = wagePerWorkerPence * requestedWorkers;
    const feePence = lockedFeePerWorker * requestedWorkers;
    const maxHoldPence = wagePence + feePence;

    // Strict bounds: actual_workers, when supplied, must be within [0, requested_workers].
    // Rejecting (instead of silently clamping) catches stale UI state and replay attempts
    // where the caller's quote inputs no longer match the stored shift.
    if (data.actual_workers != null) {
      if (data.actual_workers < 0 || data.actual_workers > requestedWorkers) {
        throw new Error(
          `actual_workers (${data.actual_workers}) must be between 0 and ${requestedWorkers} for this shift.`,
        );
      }
    }
    const actualWorkers = data.actual_workers ?? requestedWorkers;
    const expectedFinalPence = (wagePerWorkerPence + lockedFeePerWorker) * actualWorkers;
    const releasedPence = Math.max(0, maxHoldPence - expectedFinalPence);


    const tierLabel = lockedFeePerWorker === 1200 ? "Standard (1–4 staff)"
      : lockedFeePerWorker === 1000 ? "Volume tier (5–19 staff)"
      : lockedFeePerWorker === 800 ? "Bulk tier (20+ staff)"
      : `£${(lockedFeePerWorker / 100).toFixed(2)} per worker`;

    return {
      shift_id: shift.id,
      hourly_rate: Number(shift.hourly_rate),
      billable_hours: billableHours,
      requested_workers: requestedWorkers,
      actual_workers: actualWorkers,
      fee_per_worker_pence: lockedFeePerWorker,
      wage_per_worker_pence: wagePerWorkerPence,
      wage_pence: wagePence,
      fee_pence: feePence,
      total_pence: maxHoldPence,
      max_hold_pence: maxHoldPence,
      expected_final_pence: expectedFinalPence,
      released_pence: releasedPence,
      next_tier: nextPlatformFeeTier(requestedWorkers),
      tier_locked: sh.locked_platform_fee_pence != null,
      tier_label: tierLabel,
      capture_status: sh.capture_status ?? null,
      captured_amount_pence: sh.captured_amount_pence ?? null,
      last_captured_at: sh.last_captured_at ?? null,
    };
  });

// One side confirms an individual booking's completion. Writes that bartender's payout when both
// sides have confirmed (venue confirms once for the whole shift; each bartender confirms own booking).
export const confirmShiftCompletion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      shift_id: z.string().uuid(),
      booking_id: z.string().uuid().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { supabaseAdmin: _admin1 } = await import("@/integrations/supabase/client.server");
    const { data: shift, error } = await _admin1
      .from("shifts")
      .select("id, venue_id, start_time, end_time, hourly_rate, role_type, status, payment_status, venue_confirmed_at, positions_needed, break_is_paid, pricing_model, stripe_payment_intent_id, locked_platform_fee_pence, platform_fee_pence, captured_amount_pence, capture_status, capture_idempotency_key, last_captured_at, applied_credit_pence, is_asap")
      .eq("id", data.shift_id)
      .maybeSingle();
    if (error || !shift) throw new Error("Engagement not found.");
    const isVenue = shift.venue_id === userId;
    if (shift.status !== "paid") throw new Error("This engagement is not ready to be confirmed.");
    if (new Date(shift.end_time as string).getTime() > Date.now()) throw new Error("You can only confirm after the shift end time.");

    const isAsap = (shift as { is_asap?: boolean }).is_asap === true;
    const scheduledHours = hoursBetween(shift.start_time as string, shift.end_time as string);
    const scheduledBillableHours = effectiveBillableHours(scheduledHours, (shift as { break_is_paid?: boolean }).break_is_paid);
    const perBartenderHourly = bartenderPayPerHour({
      hourly_rate: Number(shift.hourly_rate),
      pricing_model: (shift as { pricing_model?: "flat_fee_v1" | "legacy_margin" }).pricing_model,
      role_type: (shift as { role_type?: string }).role_type,
    });
    // For non-ASAP: uniform per-bartender amount based on scheduled hours.
    const scheduledPerBartenderPence = Math.round(perBartenderHourly * scheduledBillableHours * 100);
    const venueHourlyPence = Math.round(Number(shift.hourly_rate) * 100);

    // ASAP-aware billable hours per booking: starts when BOTH parties confirm
    // arrival at the venue, runs through the scheduled end time.
    const billableHoursForBooking = (b: { arrival_venue_confirmed_at?: string | null; arrival_bartender_confirmed_at?: string | null }): number => {
      if (!isAsap) return scheduledBillableHours;
      const v = b.arrival_venue_confirmed_at ? new Date(b.arrival_venue_confirmed_at).getTime() : 0;
      const t = b.arrival_bartender_confirmed_at ? new Date(b.arrival_bartender_confirmed_at).getTime() : 0;
      const effectiveStart = Math.max(v, t);
      if (!effectiveStart) return 0; // ASAP with no arrival confirmation → no pay
      const endMs = new Date(shift.end_time as string).getTime();
      const raw = Math.max(0, (endMs - effectiveStart) / 3_600_000);
      return effectiveBillableHours(raw, (shift as { break_is_paid?: boolean }).break_is_paid);
    };

    if (isVenue) {
      const nowIso = new Date().toISOString();
      if (!shift.venue_confirmed_at) {
        await supabase.from("shifts").update({ venue_confirmed_at: nowIso }).eq("id", shift.id);
      }
      const { data: bookings } = await supabase
        .from("bookings")
        .select("id, bartender_id, bartender_confirmed_at, completed_at, late_deduction_pence, arrival_venue_confirmed_at, arrival_bartender_confirmed_at")
        .eq("shift_id", shift.id)
        .eq("status", "accepted");
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      let completedCount = 0;
      // Per-booking venue billable + payout, summed for the Stripe capture below.
      let totalVenueWagePence = 0;
      for (const b of bookings ?? []) {
        if (b.completed_at) continue;
        const bookingUpdate: { completed_at: string; bartender_confirmed_at?: string; auto_confirmed?: boolean } = { completed_at: nowIso };
        if (!b.bartender_confirmed_at) {
          bookingUpdate.bartender_confirmed_at = nowIso;
          bookingUpdate.auto_confirmed = true;
        }
        await supabase.from("bookings").update(bookingUpdate).eq("id", b.id);

        const billable = billableHoursForBooking(b);
        const bookingBartenderPence = Math.round(perBartenderHourly * billable * 100);
        const bookingVenueWagePence = Math.round(venueHourlyPence * billable);
        totalVenueWagePence += bookingVenueWagePence;

        const deduction = Math.max(0, Math.min(bookingBartenderPence, Number((b as { late_deduction_pence?: number }).late_deduction_pence ?? 0)));
        const payoutPence = Math.max(0, bookingBartenderPence - deduction);
        await supabaseAdmin.from("payouts").insert({
          shift_id: shift.id,
          bartender_id: b.bartender_id,
          amount_pence: payoutPence,
          status: "payable",
          kind: "base",
          notes: isAsap
            ? `ASAP shift — billed from arrival to shift end (${billable.toFixed(2)}h).${deduction > 0 ? ` £${(deduction / 100).toFixed(2)} late deduction applied.` : ""}`
            : (deduction > 0
                ? `Confirmed; £${(deduction / 100).toFixed(2)} late-arrival deduction applied.`
                : (b.bartender_confirmed_at ? "Confirmed by both parties" : "Auto-confirmed on venue confirmation")),
        }).select();
        await sendCompletionNotifications(shift, b.bartender_id as string, payoutPence, b.id as string);
        completedCount++;
      }

      // Partial-capture the pre-authorized PaymentIntent for the ACTUAL workers
      // who completed (or were auto-confirmed). Idempotent: we persist a stable
      // capture_idempotency_key on the shift and reuse it on every retry, AND
      // we re-check the PaymentIntent's live status before calling capture so
      // a second confirmShiftCompletion can never double-capture.
      const sh = shift as {
        stripe_payment_intent_id?: string | null;
        locked_platform_fee_pence?: number | null;
        platform_fee_pence?: number | null;
        captured_amount_pence?: number | null;
        capture_status?: string | null;
        capture_idempotency_key?: string | null;
        applied_credit_pence?: number | null;
      };
      if (sh.stripe_payment_intent_id && sh.capture_status !== "captured" && sh.capture_status !== "partially_captured" && sh.capture_status !== "released") {
        try {
          const actualWorkers = (bookings ?? []).length;
          const requestedWorkers = (shift.positions_needed as number) ?? actualWorkers;
          const lockedFee = sh.locked_platform_fee_pence ?? sh.platform_fee_pence ?? PLATFORM_FEE_PENCE;
          const scheduledWagePerWorkerPence = Math.round(Number(shift.hourly_rate) * scheduledBillableHours * 100);
          const appliedCredit = Math.max(0, Number(sh.applied_credit_pence ?? 0));
          // Pre-auth ceiling = scheduled wage × requested workers + locked fees.
          // ASAP final wage is the sum of per-booking (arrival → end) wages.
          const grossMax = (scheduledWagePerWorkerPence + lockedFee) * requestedWorkers;
          const grossFinal = (isAsap ? totalVenueWagePence : scheduledWagePerWorkerPence * actualWorkers) + lockedFee * actualWorkers;
          const maxAuthPence = Math.max(0, grossMax - appliedCredit);
          const finalCapturePence = Math.max(0, Math.min(maxAuthPence, grossFinal - appliedCredit));



          // Persist a stable idempotency key BEFORE the Stripe call so a crash
          // mid-capture is recoverable: the next retry reuses the same key and
          // Stripe returns the original result rather than re-charging.
          const { randomUUID } = await import("crypto");
          const idemKey = sh.capture_idempotency_key ?? randomUUID();
          if (!sh.capture_idempotency_key) {
            await supabase.from("shifts")
              .update({ capture_idempotency_key: idemKey, capture_status: "pending" })
              .eq("id", shift.id);
          }

          const env: StripeEnv = process.env.STRIPE_LIVE_API_KEY ? "live" : "sandbox";
          const stripe = createStripeClient(env);

          // Live-check the PI status. If Stripe already shows succeeded/canceled,
          // we sync our DB without a second capture call.
          const pi = await stripe.paymentIntents.retrieve(sh.stripe_payment_intent_id);
          const nowIso2 = new Date().toISOString();

          if (pi.status === "succeeded") {
            await supabase.from("shifts").update({
              captured_amount_pence: pi.amount_received ?? finalCapturePence,
              capture_status: (pi.amount_received ?? finalCapturePence) < maxAuthPence ? "partially_captured" : "captured",
              last_captured_at: nowIso2,
              capture_error: null,
            }).eq("id", shift.id);
          } else if (pi.status === "canceled") {
            await supabase.from("shifts").update({
              captured_amount_pence: 0,
              capture_status: "released",
              last_captured_at: nowIso2,
              capture_error: null,
            }).eq("id", shift.id);
          } else if (pi.status === "requires_capture" && finalCapturePence > 0) {
            await stripe.paymentIntents.capture(
              sh.stripe_payment_intent_id,
              { amount_to_capture: finalCapturePence },
              { idempotencyKey: `shift_capture_${shift.id}_${idemKey}` },
            );
            await supabase.from("shifts").update({
              captured_amount_pence: finalCapturePence,
              capture_status: finalCapturePence < maxAuthPence ? "partially_captured" : "captured",
              last_captured_at: nowIso2,
              capture_error: null,
            }).eq("id", shift.id);
          } else if (pi.status === "requires_capture" && finalCapturePence === 0) {
            // No staff worked — cancel the authorization (releases held funds).
            await stripe.paymentIntents.cancel(sh.stripe_payment_intent_id);
            await supabase.from("shifts").update({
              captured_amount_pence: 0,
              capture_status: "released",
              last_captured_at: nowIso2,
              capture_error: null,
            }).eq("id", shift.id);
          }
        } catch (e) {
          // Never block confirmation/payouts on capture issues — log and surface
          // via audit. Status flips to "failed" so ops can retry safely; the
          // idempotency key persists so the retry won't double-capture.
          const msg = e instanceof Error ? e.message : "Unknown capture error";
          console.error("paymentIntents.capture failed", e);
          await supabase.from("shifts")
            .update({ capture_status: "failed", capture_error: msg })
            .eq("id", shift.id);
        }
      }

      await maybeCloseShift(supabase, shift.id);
      return { ok: true, completed: completedCount > 0 };
    }


    // Bartender side: must own one of the bookings for this shift.
    const { data: myBooking, error: mbErr } = await supabase
      .from("bookings")
      .select("id, bartender_id, bartender_confirmed_at, completed_at, late_deduction_pence, arrival_venue_confirmed_at, arrival_bartender_confirmed_at")
      .eq("shift_id", shift.id)
      .eq("bartender_id", userId)
      .eq("status", "accepted")
      .maybeSingle();
    if (mbErr || !myBooking) throw new Error("You are not part of this engagement.");
    if (myBooking.completed_at) return { ok: true, completed: false };

    if (!myBooking.bartender_confirmed_at) {
      await supabase.from("bookings")
        .update({ bartender_confirmed_at: new Date().toISOString() })
        .eq("id", myBooking.id);
    }

    let didComplete = false;
    if (shift.venue_confirmed_at) {
      await supabase.from("bookings")
        .update({ completed_at: new Date().toISOString() })
        .eq("id", myBooking.id);
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const myBillable = billableHoursForBooking(myBooking as { arrival_venue_confirmed_at?: string | null; arrival_bartender_confirmed_at?: string | null });
      const myPerBartenderPence = Math.round(perBartenderHourly * myBillable * 100);
      const deduction = Math.max(0, Math.min(myPerBartenderPence, Number((myBooking as { late_deduction_pence?: number }).late_deduction_pence ?? 0)));
      const payoutPence = Math.max(0, myPerBartenderPence - deduction);
      await supabaseAdmin.from("payouts").insert({
        shift_id: shift.id,
        bartender_id: userId,
        amount_pence: payoutPence,
        status: "payable",
        kind: "base",
        notes: isAsap
          ? `ASAP shift — billed from arrival to shift end (${myBillable.toFixed(2)}h).${deduction > 0 ? ` £${(deduction / 100).toFixed(2)} late deduction applied.` : ""}`
          : (deduction > 0 ? `Confirmed; £${(deduction / 100).toFixed(2)} late-arrival deduction applied.` : "Confirmed by both parties"),
      });

      await sendCompletionNotifications(shift, userId, payoutPence, myBooking.id as string);
      didComplete = true;
      await maybeCloseShift(supabase, shift.id);
    }
    return { ok: true, completed: didComplete };
  });

async function maybeCloseShift(
  supabase: { from: (t: string) => any },
  shiftId: string,
) {
  const { count: outstanding } = await supabase
    .from("bookings")
    .select("id", { count: "exact", head: true })
    .eq("shift_id", shiftId)
    .is("completed_at", null);
  if ((outstanding ?? 0) === 0) {
    await supabase.from("shifts").update({ status: "completed", completed_at: new Date().toISOString() }).eq("id", shiftId);
  }
}

async function sendCompletionNotifications(
  shift: { id: string; venue_id: string | null },
  bartenderId: string,
  payoutPence: number,
  bookingId?: string,
) {
  try {
    const { notify } = await import("@/lib/notify.server");
    const amount = `\u00a3${(payoutPence / 100).toFixed(2)}`;
    const receiptLink = bookingId ? `/receipt/${bookingId}` : undefined;
    await notify({ userId: bartenderId, type: "shift_completed", subjectId: shift.id,
      title: "Shift completed", body: "Both parties confirmed the engagement. Nice work.", link: "/bartender" });
    if (shift.venue_id) {
      await notify({ userId: shift.venue_id, type: "shift_completed", subjectId: shift.id,
        title: "Shift completed", body: "Both parties confirmed an engagement.", link: "/venue" });
    }
    await notify({ userId: bartenderId, type: "payout_sent", subjectId: shift.id,
      title: "Payout released", body: `${amount} has been queued for payout.`, link: "/bartender" });
    if (shift.venue_id) {
      await notify({ userId: shift.venue_id, type: "review_request", subjectId: shift.id,
        title: "Leave a review", body: "Help other venues — rate your bartender for this engagement.", link: `/venue?review=${shift.id}` });
    }
    if (bookingId && receiptLink) {
      await notify({ userId: bartenderId, type: "booking_receipt", subjectId: `${bookingId}:receipt`,
        title: "Your booking receipt",
        body: `Payout ${amount}. Open the receipt to view the full breakdown and save as PDF.`,
        link: receiptLink, ctaLabel: "View receipt" });
      if (shift.venue_id) {
        await notify({ userId: shift.venue_id, type: "booking_receipt", subjectId: `${bookingId}:receipt`,
          title: "Booking receipt",
          body: `Engagement complete. Open the receipt to view the full breakdown and save as PDF.`,
          link: receiptLink, ctaLabel: "View receipt" });
      }
    }
  } catch (e) { console.error("notify completion failed", e); }
}

export const CANCEL_REASONS = [
  "illness",
  "emergency",
  "venue_change",
  "double_booked",
  "transport_issue",
  "no_longer_needed",
  "other",
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

// Cancel an engagement. Venue cancels whole shift; bartender cancels own booking.
export const cancelShift = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      shift_id: z.string().uuid(),
      environment: z.enum(["sandbox", "live"]),
      reason: z.enum(CANCEL_REASONS),
      reason_text: z.string().trim().max(500).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ ok: true; refund_pence: number; strike_issued: boolean; strike_number: number; suspended: boolean }>> => {
    try {
      const { supabase, userId } = context;

      // Block suspended accounts.
      const { data: actor } = await supabase
        .from("profiles").select("is_suspended").eq("id", userId).maybeSingle();
      if (actor?.is_suspended) throw new Error("Your account is suspended. Submit an appeal to restore access.");

      const { data: shift, error } = await supabase
        .from("shifts")
        .select("id, venue_id, bartender_id, start_time, end_time, hourly_rate, status, payment_status, stripe_payment_intent_id, positions_needed, break_is_paid, pricing_model, platform_fee_pence")
        .eq("id", data.shift_id)
        .maybeSingle();
      if (error || !shift) throw new Error("Engagement not found.");
      const isVenue = shift.venue_id === userId;

      const { data: myBooking } = isVenue ? { data: null } : await supabase
        .from("bookings")
        .select("id, status")
        .eq("shift_id", shift.id)
        .eq("bartender_id", userId)
        .maybeSingle();

      const isBartender = !!myBooking;
      if (!isVenue && !isBartender) throw new Error("You are not part of this engagement.");
      if (!["open", "awaiting_payment", "paid"].includes(shift.status as string)) {
        throw new Error("This engagement can no longer be cancelled.");
      }

      // Capture accepted bartenders BEFORE we mutate booking statuses so we can notify them.
      const { data: acceptedBeforeCancel } = await supabase
        .from("bookings")
        .select("bartender_id")
        .eq("shift_id", shift.id)
        .eq("status", "accepted");
      const acceptedBartenderIds = (acceptedBeforeCancel ?? [])
        .map((r) => (r as { bartender_id: string | null }).bartender_id)
        .filter((id): id is string => !!id);

      const hours = hoursBetween(shift.start_time as string, shift.end_time as string);
      const billableHours = effectiveBillableHours(hours, (shift as { break_is_paid?: boolean }).break_is_paid);
      const positions = (shift.positions_needed as number) ?? 1;
      const pricingModel = (shift as { pricing_model?: "flat_fee_v1" | "legacy_margin" }).pricing_model ?? "flat_fee_v1";
      const platformFee = pricingModel === "flat_fee_v1"
        ? ((shift as { platform_fee_pence?: number }).platform_fee_pence ?? PLATFORM_FEE_PENCE)
        : 0;
      const { wagePence, feePence, totalPence } = computeVenueTotalPence({
        hourly_rate: Number(shift.hourly_rate),
        billable_hours: billableHours,
        positions,
        pricing_model: pricingModel,
        platform_fee_pence: platformFee,
      });
      const hoursToStart = (new Date(shift.start_time as string).getTime() - Date.now()) / 3_600_000;

      // ---- Payment ledger check -------------------------------------------
      // Refund amounts and refund emails may only ever be produced when a real
      // settled Stripe charge exists for this shift. A shift marked "paid" with
      // no payment intent (or an intent Stripe says never succeeded) yields
      // nothing: no refund call, no refund amount, no refund wording.
      const intentId = (shift.stripe_payment_intent_id as string | null) ?? null;
      let hasCharge = shift.payment_status === "paid" && !!intentId;
      if (hasCharge && intentId) {
        try {
          const stripe = createStripeClient(data.environment as StripeEnv);
          const pi = await stripe.paymentIntents.retrieve(intentId);
          const received = typeof pi.amount_received === "number" ? pi.amount_received : 0;
          hasCharge = (pi.status === "succeeded" || received > 0) && received > 0;
        } catch (e) {
          // Ledger unreachable → fail closed on money movement.
          console.error("payment ledger check failed", e);
          hasCharge = false;
        }
      }

      const outcome = computeCancellationOutcome({
        isVenue,
        acceptedCount: acceptedBartenderIds.length,
        hoursToStart,
        hasCharge,
        wagePence,
        feePence,
        totalPence,
      });
      const within24h = isVenue ? outcome.within24h : hoursToStart < 24;
      const alreadyPaid = hasCharge;

      let refundPence = outcome.refundPence;
      let note = outcome.note;



      if (isBartender && !isVenue) {
        await supabase.from("bookings")
          .update({ status: "denied", cancelled_at: new Date().toISOString() })
          .eq("id", (myBooking as { id: string }).id);

        const perBartenderPence = Math.round(Number(shift.hourly_rate) * billableHours * 100);
        if (alreadyPaid && shift.stripe_payment_intent_id && perBartenderPence > 0) {
          const stripe = createStripeClient(data.environment as StripeEnv);
          await stripe.refunds.create({
            payment_intent: shift.stripe_payment_intent_id as string,
            amount: perBartenderPence,
            metadata: { shift_id: shift.id, bartender_id: userId, reason: data.reason },
          });
        }
        refundPence = alreadyPaid ? perBartenderPence : 0;

        // Re-open shift so the slot returns to the job board instantly.
        const { count: remainingAccepted } = await supabase
          .from("bookings")
          .select("id", { count: "exact", head: true })
          .eq("shift_id", shift.id)
          .eq("status", "accepted");
        const stillFilled = (remainingAccepted ?? 0) >= positions;
        if (!stillFilled) {
          await supabase.from("shifts").update({
            status: "open",
            venue_confirmed_at: null,
            bartender_confirmed_at: null,
            favorites_only_until: null,
            ...(shift.bartender_id === userId ? { bartender_id: null } : {}),
            ...(shift.payment_status === "paid" ? { payment_status: "partially_refunded" as const } : {}),
          }).eq("id", shift.id);
        }
      } else {
        // Venue cancelling whole engagement.
        //
        // Money only changes hands when a bartender was already confirmed AND
        // the shift starts within 24h: the confirmed staff keep 50% of their
        // wage and the platform keeps its per-worker fee. Everything else is
        // refunded. A listing with nobody confirmed costs the venue nothing.
        const compensationPence = outcome.compensationPence;
        if (within24h) {
          refundPence = outcome.refundPence;
          note = outcome.note;
        }

        if (alreadyPaid && shift.stripe_payment_intent_id && refundPence > 0) {
          const stripe = createStripeClient(data.environment as StripeEnv);
          await stripe.refunds.create({
            payment_intent: shift.stripe_payment_intent_id as string,
            amount: refundPence,
            metadata: { shift_id: shift.id, reason: note },
          });
        }
        const newPaymentStatus =
          alreadyPaid
            ? refundPence >= totalPence ? "refunded" : "partially_refunded"
            : shift.payment_status;
        await supabase.from("shifts").update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          cancelled_by: "venue",
          payment_status: newPaymentStatus,
        }).eq("id", shift.id);
        await supabase.from("bookings")
          .update({ cancelled_at: new Date().toISOString(), status: "denied" })
          .eq("shift_id", shift.id)
          .neq("status", "denied");

        // Venue hadn't paid yet but owes the confirmed staff and the platform:
        // raise a late-cancellation fee. They can't post new jobs until it's settled.
        if (within24h && !alreadyPaid && compensationPence > 0) {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          await supabaseAdmin.from("late_cancellation_fees").upsert({
            venue_id: userId,
            shift_id: shift.id,
            total_pence: totalPence,
            amount_pence: compensationPence,
            status: "outstanding",
          }, { onConflict: "shift_id" });
        }
      }


      // Timeline entry for admins.
      await supabase.from("shift_timeline").insert({
        shift_id: shift.id,
        actor_user_id: userId,
        actor_role: isVenue ? "venue" : "bartender",
        event_type: isVenue ? "shift_cancelled" : "bartender_dropped_out",
        details: {
          reason: data.reason,
          reason_text: data.reason_text ?? null,
          within_24h: within24h,
          hours_to_start: Number(hoursToStart.toFixed(2)),
          refund_pence: refundPence,
          reopened: !isVenue,
        },
      });

      // Audit log + strike if within 24h.
      let strikeIssued = false;
      let strikeNumber = 0;
      let suspended = false;

      const { data: auditRow, error: auditError } = await supabase
        .from("cancellation_audit_log")
        .insert({
          shift_id: shift.id,
          cancelled_by_user_id: userId,
          cancelled_by_role: isVenue ? "venue" : "bartender",
          reason: data.reason,
          reason_text: data.reason_text ?? null,
          hours_before_start: Number(hoursToStart.toFixed(2)),
          within_24h: within24h,
          strike_issued: within24h,
        })
        .select("id")
        .single();

      if (auditError) {
        // Row-level security rejections here silently broke cancellations in
        // the past. Alert the admin inbox instead of failing quietly.
        const { reportCancellationFailure } = await import("@/lib/cancellation-alerts.server");
        await reportCancellationFailure({
          stage: "cancellation_audit_log insert",
          shiftId: shift.id,
          userId,
          role: isVenue ? "venue" : "bartender",
          code: auditError.code ?? null,
          message: auditError.message,
          details: auditError.details ?? null,
        });
      }

      if (within24h && auditRow) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { count } = await supabaseAdmin
          .from("user_strikes")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("status", "active");
        strikeNumber = (count ?? 0) + 1;
        const reasonLabels: Record<number, string> = {
          1: "First strike — warning: repeat late cancellations may lead to suspension.",
          2: "Second strike — FINAL warning: the next late cancellation will suspend your account.",
          3: "Third strike — account suspended.",
        };
        await supabaseAdmin.from("user_strikes").insert({
          user_id: userId,
          strike_number: Math.min(strikeNumber, 3),
          audit_log_id: auditRow.id,
          reason: reasonLabels[Math.min(strikeNumber, 3)] ?? reasonLabels[3],
        });
        strikeIssued = true;
        if (strikeNumber >= 3) {
          await supabaseAdmin.from("profiles").update({
            is_suspended: true,
            suspended_at: new Date().toISOString(),
            suspension_reason: "Three late-cancellation strikes accumulated.",
          }).eq("id", userId);
          suspended = true;
        }
      }

      // Notify affected parties (in-app + email + SMS via notify()).
      try {
        const { notify } = await import("@/lib/notify.server");
        const whenLabel = new Date(shift.start_time as string).toLocaleString("en-GB");
        // Ledger-gated: only mention money when a real charge was refunded.
        const refundLabel = hasCharge && refundPence > 0
          ? ` Refund: £${(refundPence / 100).toFixed(2)}.`
          : "";

        if (isVenue) {
          // Venue cancelled whole engagement → tell each accepted bartender.
          for (const bId of acceptedBartenderIds) {
            await notify({
              userId: bId, type: "shift_cancelled", subjectId: shift.id,
              title: "Engagement cancelled by venue",
              body: `Your ${whenLabel} engagement was cancelled by the venue.${refundLabel}`,
              link: "/bartender",
            });
          }
          if (shift.venue_id) {
            await notify({
              userId: shift.venue_id as string, type: "shift_cancelled", subjectId: shift.id,
              title: "Engagement cancelled",
              body: `You cancelled the ${whenLabel} engagement.${refundLabel}`,
              link: "/venue",
            });
          }
        } else {
          // Bartender dropped out → tell venue (shift may have reopened) + the bartender.
          if (shift.venue_id) {
            await notify({
              userId: shift.venue_id as string, type: "shift_cancelled", subjectId: `${shift.id}:${userId}`,
              title: "Bartender cancelled",
              body: `A bartender dropped out of your ${whenLabel} engagement. We've reposted the slot if it isn't fully staffed.`,
              link: "/venue",
            });
          }
          await notify({
            userId, type: "shift_cancelled", subjectId: `${shift.id}:${userId}`,
            title: "Cancellation confirmed",
            body: `You cancelled your ${whenLabel} engagement.${refundLabel}`,
            link: "/bartender",
          });
        }
      } catch (e) { console.error("notify shift_cancelled failed", e); }

      return { ok: true, refund_pence: refundPence, strike_issued: strikeIssued, strike_number: strikeNumber, suspended };
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });

// ---------- Strikes & Appeals ----------

export const getMyStrikes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: strikes } = await supabase
      .from("user_strikes")
      .select("id, strike_number, reason, status, created_at, audit_log_id")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    const { data: appeals } = await supabase
      .from("strike_appeals")
      .select("id, strike_id, status, description, evidence_path, admin_notes, created_at, reviewed_at")
      .eq("user_id", userId);
    const { data: profile } = await supabase
      .from("profiles")
      .select("is_suspended, suspended_at, suspension_reason")
      .eq("id", userId)
      .maybeSingle();
    return { strikes: strikes ?? [], appeals: appeals ?? [], profile };
  });

export const submitStrikeAppeal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      strike_id: z.string().uuid(),
      description: z.string().trim().min(20, "Please describe what happened in at least 20 characters.").max(2000),
      evidence_path: z.string().trim().max(500).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ ok: true }>> => {
    try {
      const { supabase, userId } = context;
      const { data: strike } = await supabase
        .from("user_strikes")
        .select("id, user_id, status")
        .eq("id", data.strike_id)
        .maybeSingle();
      if (!strike || strike.user_id !== userId) throw new Error("Strike not found.");
      if (strike.status !== "active") throw new Error("This strike is no longer eligible for appeal.");
      const { data: inserted, error } = await supabase.from("strike_appeals").insert({
        strike_id: data.strike_id,
        user_id: userId,
        description: data.description,
        evidence_path: data.evidence_path ?? null,
      }).select("id").single();
      if (error) throw new Error(error.message);

      // Notify all admins so they can review.
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: admins } = await supabaseAdmin
          .from("user_roles").select("user_id").eq("role", "admin");
        if (admins && admins.length > 0) {
          await supabaseAdmin.from("notifications").insert(
            admins.map((a: { user_id: string }) => ({
              user_id: a.user_id,
              type: "appeal_submitted",
              title: "New strike appeal awaiting review",
              body: "A member has submitted an appeal against a cancellation strike.",
              link: "/admin/appeals",
              metadata: { appeal_id: inserted?.id, strike_id: data.strike_id },
            })),
          );
        }
      } catch (e) { console.error("notify admins failed", e); }
      return { ok: true };
    } catch (err) {
      return { error: err instanceof Error ? err.message : "Could not submit appeal." };
    }
  });

// ---------- Admin appeal review ----------

export const listPendingAppeals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: appeals } = await supabaseAdmin
      .from("strike_appeals")
      .select(`
        id, status, description, evidence_path, admin_notes, created_at, reviewed_at, user_id, strike_id,
        user_strikes!inner(id, strike_number, reason, status, audit_log_id, created_at)
      `)
      .order("created_at", { ascending: true });

    const userIds = Array.from(new Set((appeals ?? []).map((a: { user_id: string }) => a.user_id)));
    const { data: profiles } = userIds.length
      ? await supabaseAdmin.from("profiles").select("id, full_name, role, is_suspended").in("id", userIds)
      : { data: [] as { id: string; full_name: string | null; role: string; is_suspended: boolean }[] };

    const auditIds = Array.from(new Set(
      (appeals ?? []).map((a) => (a as { user_strikes: { audit_log_id: string | null } }).user_strikes?.audit_log_id).filter(Boolean) as string[],
    ));
    const { data: audits } = auditIds.length
      ? await supabaseAdmin.from("cancellation_audit_log")
          .select("id, shift_id, reason, reason_text, hours_before_start, cancelled_by_role, created_at")
          .in("id", auditIds)
      : { data: [] as Array<{ id: string; shift_id: string; reason: string; reason_text: string | null; hours_before_start: number | null; cancelled_by_role: string; created_at: string }> };

    // Sign evidence URLs for admin review.
    const signed: Record<string, string> = {};
    for (const a of appeals ?? []) {
      const p = (a as { evidence_path: string | null }).evidence_path;
      if (p) {
        const { data: s } = await supabaseAdmin.storage.from("appeal-evidence").createSignedUrl(p, 3600);
        if (s?.signedUrl) signed[p] = s.signedUrl;
      }
    }
    return { appeals: appeals ?? [], profiles: profiles ?? [], audits: audits ?? [], signed };
  });

export const reviewAppeal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      appeal_id: z.string().uuid(),
      decision: z.enum(["approved", "denied"]),
      admin_notes: z.string().trim().max(2000).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ ok: true }>> => {
    try {
      const { supabase, userId } = context;
      const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
      if (!isAdmin) throw new Error("Forbidden");

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: appeal } = await supabaseAdmin
        .from("strike_appeals")
        .select("id, user_id, strike_id, status")
        .eq("id", data.appeal_id)
        .maybeSingle();
      if (!appeal) throw new Error("Appeal not found.");
      if (appeal.status !== "pending") throw new Error("This appeal has already been reviewed.");

      const reviewedAt = new Date().toISOString();
      await supabaseAdmin.from("strike_appeals").update({
        status: data.decision,
        admin_notes: data.admin_notes ?? null,
        reviewed_at: reviewedAt,
        reviewed_by: userId,
      }).eq("id", data.appeal_id);

      const { data: strike } = await supabaseAdmin
        .from("user_strikes")
        .select("id, audit_log_id, status")
        .eq("id", appeal.strike_id)
        .maybeSingle();

      if (data.decision === "approved" && strike) {
        await supabaseAdmin.from("user_strikes").update({ status: "overturned" }).eq("id", strike.id);
        // If user was suspended and now has fewer than 3 active strikes, lift suspension.
        const { count } = await supabaseAdmin
          .from("user_strikes")
          .select("id", { count: "exact", head: true })
          .eq("user_id", appeal.user_id)
          .eq("status", "active");
        if ((count ?? 0) < 3) {
          await supabaseAdmin.from("profiles").update({
            is_suspended: false,
            suspended_at: null,
            suspension_reason: null,
          }).eq("id", appeal.user_id);
        }
      }

      // Record outcome on the originating audit log row.
      if (strike?.audit_log_id) {
        await supabaseAdmin.from("cancellation_audit_log").update({
          appeal_outcome: data.decision,
          appeal_reviewed_by: userId,
          appeal_reviewed_at: reviewedAt,
          appeal_admin_notes: data.admin_notes ?? null,
        }).eq("id", strike.audit_log_id);
      }

      // Notify the user.
      await supabaseAdmin.from("notifications").insert({
        user_id: appeal.user_id,
        type: "appeal_reviewed",
        title: data.decision === "approved" ? "Appeal approved" : "Appeal denied",
        body: data.decision === "approved"
          ? "Your strike has been overturned. Account access has been restored if applicable."
          : "Your appeal was reviewed and the strike remains on your account.",
        link: "/bartender",
        metadata: { appeal_id: appeal.id, decision: data.decision },
      });

      return { ok: true };
    } catch (err) {
      return { error: err instanceof Error ? err.message : "Could not review appeal." };
    }
  });

export const getIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    return { isAdmin: !!data };
  });

// List outstanding late-cancellation fees owed by the current venue.
export const listOutstandingLateFees = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("late_cancellation_fees")
      .select("id, shift_id, total_pence, amount_pence, status, created_at")
      .eq("venue_id", userId)
      .eq("status", "outstanding")
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// Start a Stripe Embedded Checkout to pay a late-cancellation fee.
export const createLateFeeCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      fee_id: z.string().uuid(),
      environment: z.enum(["sandbox", "live"]),
      return_url: z.string().url(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ clientSecret: string }>> => {
    try {
      const { supabase, userId } = context;
      const { data: fee, error } = await supabase
        .from("late_cancellation_fees")
        .select("id, venue_id, shift_id, amount_pence, status")
        .eq("id", data.fee_id)
        .maybeSingle();
      if (error || !fee) throw new Error("Late cancellation fee not found.");
      if (fee.venue_id !== userId) throw new Error("Only the venue that owes this fee may pay it.");
      if (fee.status !== "outstanding") throw new Error("This fee is no longer outstanding.");
      if ((fee.amount_pence as number) < 100) throw new Error("Fee amount is too low to charge.");

      const stripe = createStripeClient(data.environment as StripeEnv);
      const session = await stripe.checkout.sessions.create({
        line_items: [{
          price_data: {
            currency: "gbp",
            product_data: { name: "Late cancellation fee (50% of engagement)" },
            unit_amount: fee.amount_pence as number,
          },
          quantity: 1,
        }],
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.return_url,
        payment_intent_data: {
          description: `Late cancellation fee for shift ${fee.shift_id}`,
          metadata: { late_fee_id: fee.id, shift_id: fee.shift_id as string, venue_id: userId },
        },
        metadata: { late_fee_id: fee.id, shift_id: fee.shift_id as string, venue_id: userId },
      });

      {
        // Late-fee rows are server-controlled: venues have no direct write access.
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin
          .from("late_cancellation_fees")
          .update({ stripe_checkout_session_id: session.id })
          .eq("id", fee.id);
      }

      return { clientSecret: session.client_secret ?? "" };
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });

// ---------- UK Tipping Act: post-shift tip allocation ----------
//
// After a shift completes, the venue chooses how to settle tips for that
// engagement: either pay them through Stripe (100% pass-through, no platform
// cut) or mark "no tips collected". This satisfies the Employment (Allocation
// of Tips) Act 2023 requirement to fairly and transparently allocate tips
// within the month following the engagement.

export const allocateShiftTips = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      shift_id: z.string().uuid(),
      action: z.enum(["pay", "none"]),
      tip_amount_pence: z.number().int().min(0).max(1_000_00).optional(),
      environment: z.enum(["sandbox", "live"]).optional(),
      return_url: z.string().url().optional(),
      idempotency_key: z.string().min(8).max(128),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ ok: true; clientSecret?: string; status: "paid_pending" | "not_applicable" | "duplicate" }>> => {
    const { supabase, userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const writeAudit = async (event: string, extra: Record<string, unknown> = {}) => {
      try {
        await supabaseAdmin.from("tip_allocation_audit_log").insert({
          shift_id: data.shift_id,
          venue_id: userId,
          actor_user_id: userId,
          actor_role: "venue",
          event,
          idempotency_key: data.idempotency_key,
          tip_amount_pence: data.tip_amount_pence ?? 0,
          ...extra,
        });
      } catch (e) { console.error("tip audit write failed", e); }
    };

    try {
      // Idempotency short-circuit on replay of the same key.
      const { data: existing } = await supabaseAdmin
        .from("tip_allocations")
        .select("id, status, stripe_checkout_session_id, action")
        .eq("idempotency_key", data.idempotency_key)
        .maybeSingle();
      if (existing) {
        await writeAudit("attempt_duplicate", { status: existing.status as string });
        if (existing.action === "none") return { ok: true, status: "not_applicable" };
        return { ok: true, status: "duplicate" };
      }

      const { data: shift } = await supabase
        .from("shifts")
        .select("id, venue_id, status, completed_at, tip_payment_status, tip_amount_pence")
        .eq("id", data.shift_id)
        .maybeSingle();
      if (!shift) throw new Error("Engagement not found.");
      if (shift.venue_id !== userId) throw new Error("Only the host venue can allocate tips for this engagement.");
      if (shift.status !== "completed") throw new Error("Tips can only be allocated after the engagement is marked complete.");
      if (shift.tip_payment_status === "paid") throw new Error("Tips for this engagement have already been paid.");

      await writeAudit("attempt", {});

      if (data.action === "none") {
        await supabaseAdmin.from("tip_allocations").insert({
          shift_id: shift.id, venue_id: userId, idempotency_key: data.idempotency_key,
          action: "none", tip_amount_pence: 0, status: "not_applicable",
        });
        await supabase
          .from("shifts")
          .update({
            tip_payment_status: "not_applicable",
            tip_amount_pence: 0,
            tip_allocated_at: new Date().toISOString(),
          })
          .eq("id", shift.id);
        await writeAudit("marked_none", { status: "not_applicable" });
        return { ok: true, status: "not_applicable" };
      }

      const tipPence = data.tip_amount_pence ?? 0;
      if (tipPence < 100) throw new Error("Tip amount must be at least £1.00.");
      if (!data.environment) throw new Error("Stripe environment is required to charge tips.");
      if (!data.return_url) throw new Error("Return URL is required to charge tips.");

      const { data: bookings } = await supabase
        .from("bookings")
        .select("bartender_id")
        .eq("shift_id", shift.id)
        .eq("status", "accepted")
        .not("completed_at", "is", null);
      const recipients = (bookings ?? []).map((b) => b.bartender_id as string).filter(Boolean);
      if (recipients.length === 0) throw new Error("No completed bartenders to tip on this engagement.");

      await supabaseAdmin.from("tip_allocations").insert({
        shift_id: shift.id, venue_id: userId, idempotency_key: data.idempotency_key,
        action: "pay", tip_amount_pence: tipPence, status: "initiated",
      });

      let session;
      try {
        const stripe = createStripeClient(data.environment as StripeEnv);
        session = await stripe.checkout.sessions.create({
          line_items: [{
            price_data: {
              currency: "gbp",
              product_data: { name: "Tip — passed 100% to bartender (UK Tipping Act 2023)" },
              unit_amount: tipPence,
            },
            quantity: 1,
          }],
          mode: "payment",
          ui_mode: "embedded_page",
          return_url: data.return_url,
          payment_intent_data: {
            description: `Tip for engagement ${shift.id}`,
            metadata: { shift_id: shift.id, venue_id: userId, kind: "tip", tip_amount_pence: String(tipPence), idempotency_key: data.idempotency_key },
          },
          metadata: { shift_id: shift.id, venue_id: userId, kind: "tip", tip_amount_pence: String(tipPence), idempotency_key: data.idempotency_key },
        }, { idempotencyKey: `tip_${data.idempotency_key}` });
      } catch (err) {
        const msg = getStripeErrorMessage(err);
        await supabaseAdmin.from("tip_allocations")
          .update({ status: "failed", error_message: msg })
          .eq("idempotency_key", data.idempotency_key);
        await writeAudit("checkout_failed", { status: "failed", error_message: msg });
        return { error: msg };
      }

      await supabaseAdmin.from("tip_allocations")
        .update({ status: "checkout_open", stripe_checkout_session_id: session.id })
        .eq("idempotency_key", data.idempotency_key);

      await supabase
        .from("shifts")
        .update({
          stripe_tip_checkout_session_id: session.id,
          tip_amount_pence: tipPence,
        })
        .eq("id", shift.id);

      await writeAudit("checkout_created", { status: "checkout_open", stripe_checkout_session_id: session.id });

      return { ok: true, status: "paid_pending", clientSecret: session.client_secret ?? "" };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Tip allocation failed.";
      await writeAudit("error", { status: "failed", error_message: msg });
      return { error: msg };
    }
  });


// ─────────────────────────────────────────────────────────────────────────────
// Admin: refund a tip allocation (refund Stripe charge + reverse any transfers
// already paid out to bartenders). Also: retry a failed tip payout sweep.
// ─────────────────────────────────────────────────────────────────────────────

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden — admin only.");
}

export const refundTipAllocation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      allocation_id: z.string().uuid(),
      environment: z.enum(["sandbox", "live"]),
      reason: z.string().min(3).max(500),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ ok: true; refunded_pence: number; reversed_payouts: number }>> => {
    try {
      const { supabase, userId } = context;
      await assertAdmin(supabase, userId);
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      const { data: alloc } = await supabaseAdmin
        .from("tip_allocations")
        .select("id, shift_id, venue_id, idempotency_key, tip_amount_pence, status, stripe_payment_intent_id")
        .eq("id", data.allocation_id)
        .maybeSingle();
      if (!alloc) throw new Error("Tip allocation not found.");
      const a = alloc as {
        id: string; shift_id: string; venue_id: string; idempotency_key: string;
        tip_amount_pence: number; status: string; stripe_payment_intent_id: string | null;
      };
      if (a.status === "refunded" || a.status === "reversed") {
        return { ok: true, refunded_pence: 0, reversed_payouts: 0 };
      }
      if (!a.stripe_payment_intent_id) {
        throw new Error("This allocation has no Stripe charge to refund.");
      }

      const stripe = createStripeClient(data.environment as StripeEnv);

      let refundedPence = 0;
      try {
        const refund = await stripe.refunds.create(
          {
            payment_intent: a.stripe_payment_intent_id,
            reason: "requested_by_customer",
            metadata: { allocation_id: a.id, admin_id: userId, reason: data.reason },
          },
          { idempotencyKey: `tip_refund_${a.id}` },
        );
        refundedPence = refund.amount ?? a.tip_amount_pence;
      } catch (err) {
        const msg = getStripeErrorMessage(err);
        await supabaseAdmin.from("tip_allocation_audit_log").insert({
          shift_id: a.shift_id, venue_id: a.venue_id, actor_user_id: userId, actor_role: "admin",
          event: "refund_failed", idempotency_key: a.idempotency_key, status: a.status,
          error_message: msg, metadata: { reason: data.reason },
        });
        return { error: msg };
      }

      // Reverse any tip payouts that already transferred out to bartenders.
      const { data: paidPayouts } = await supabaseAdmin
        .from("payouts")
        .select("id, bartender_id, amount_pence, stripe_transfer_id")
        .eq("shift_id", a.shift_id)
        .eq("kind", "tip")
        .eq("status", "paid_out")
        .not("stripe_transfer_id", "is", null);
      let reversed = 0;
      for (const row of (paidPayouts ?? []) as Array<{ id: string; bartender_id: string; amount_pence: number; stripe_transfer_id: string }>) {
        try {
          await stripe.transfers.createReversal(
            row.stripe_transfer_id,
            { metadata: { allocation_id: a.id, payout_id: row.id, admin_id: userId } },
            { idempotencyKey: `tip_reverse_${row.id}` },
          );
          await supabaseAdmin.from("payouts").update({
            status: "reversed" as never,
            failure_reason: `Reversed by admin — ${data.reason}`,
          }).eq("id", row.id);
          reversed++;
          try {
            const { notify } = await import("@/lib/notify.server");
            await notify({
              userId: row.bartender_id, type: "payout_sent", subjectId: `tip-reversed:${row.id}`,
              title: "Tip payout reversed",
              body: `A previous tip payout of \u00a3${(row.amount_pence / 100).toFixed(2)} has been reversed by our team. Reason: ${data.reason}`,
              link: "/bartender/tips",
            });
          } catch { /* swallow */ }
        } catch (err) {
          const msg = getStripeErrorMessage(err);
          await supabaseAdmin.from("tip_allocation_audit_log").insert({
            shift_id: a.shift_id, venue_id: a.venue_id, actor_user_id: userId, actor_role: "admin",
            event: "reversal_failed", idempotency_key: a.idempotency_key,
            error_message: msg, metadata: { payout_id: row.id, transfer_id: row.stripe_transfer_id },
          });
        }
      }

      const nextStatus = reversed > 0 ? "reversed" : "refunded";
      await supabaseAdmin.from("tip_allocations").update({
        status: nextStatus,
        error_message: data.reason,
      }).eq("id", a.id);

      await supabaseAdmin.from("shifts").update({
        tip_payment_status: "refunded",
      }).eq("id", a.shift_id);

      await supabaseAdmin.from("tip_allocation_audit_log").insert({
        shift_id: a.shift_id, venue_id: a.venue_id, actor_user_id: userId, actor_role: "admin",
        event: nextStatus, idempotency_key: a.idempotency_key, tip_amount_pence: refundedPence,
        status: nextStatus, metadata: { reason: data.reason, reversed_payouts: reversed },
      });

      return { ok: true, refunded_pence: refundedPence, reversed_payouts: reversed };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Refund failed.";
      return { error: msg };
    }
  });

export const retryTipPayout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      allocation_id: z.string().uuid(),
      environment: z.enum(["sandbox", "live"]),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ ok: true; processed: number; paid: number; failed: number; skipped: number }>> => {
    try {
      const { supabase, userId } = context;
      await assertAdmin(supabase, userId);
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      const { data: alloc } = await supabaseAdmin
        .from("tip_allocations")
        .select("id, shift_id, idempotency_key, status")
        .eq("id", data.allocation_id)
        .maybeSingle();
      if (!alloc) throw new Error("Tip allocation not found.");
      const a = alloc as { id: string; shift_id: string; idempotency_key: string; status: string };

      // Reset failed tip payouts on this shift so the sweep can pick them up again.
      const { data: failedPayouts } = await supabaseAdmin
        .from("payouts")
        .select("id, attempts")
        .eq("shift_id", a.shift_id)
        .eq("kind", "tip")
        .eq("status", "failed");
      const ids = (failedPayouts ?? []).map((p: { id: string }) => p.id);
      if (ids.length === 0) {
        throw new Error("No failed tip payouts to retry on this allocation.");
      }
      await supabaseAdmin.from("payouts")
        .update({ status: "payable", attempts: 0, failure_reason: null })
        .in("id", ids);

      await supabaseAdmin.from("tip_allocation_audit_log").insert({
        shift_id: a.shift_id, actor_user_id: userId, actor_role: "admin",
        event: "retry_initiated", idempotency_key: a.idempotency_key, status: a.status,
        metadata: { reset_payouts: ids.length },
      });

      const { processPayablePayoutsCore } = await import("@/lib/payouts.server");
      const result = await processPayablePayoutsCore(data.environment as StripeEnv);

      // Refresh allocation status based on outcome of the tip payouts.
      const { data: post } = await supabaseAdmin
        .from("payouts").select("status").in("id", ids);
      const allPaid = (post ?? []).length > 0 && (post ?? []).every((p: { status: string }) => p.status === "paid_out");
      const anyFailed = (post ?? []).some((p: { status: string }) => p.status === "failed");
      const nextStatus = allPaid ? "payout_succeeded" : anyFailed ? "payout_failed" : null;
      if (nextStatus) {
        await supabaseAdmin.from("tip_allocations").update({
          status: nextStatus,
          payout_completed_at: allPaid ? new Date().toISOString() : null,
        }).eq("id", a.id);
      }
      await supabaseAdmin.from("tip_allocation_audit_log").insert({
        shift_id: a.shift_id, actor_user_id: userId, actor_role: "admin",
        event: "retry_completed", idempotency_key: a.idempotency_key,
        status: nextStatus ?? a.status, metadata: result as never,
      });

      return { ok: true, ...result };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Retry failed.";
      return { error: msg };
    }
  });

// ---------- Capture retry & admin shift detail ----------

/**
 * Re-run the partial-capture path for a shift whose capture is stuck in
 * `pending` or `failed`. Reuses the persisted `capture_idempotency_key`, so
 * Stripe returns the original result if the previous call actually
 * succeeded — there is no risk of a double-capture.
 *
 * Callers: the host venue, or any admin with finance capability.
 */
export const retryShiftCapture = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ shift_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<Result<{ ok: true; capture_status: string; captured_amount_pence: number | null }>> => {
    try {
      const { supabase, userId } = context;
      const { data: shift, error } = await supabase
        .from("shifts")
        .select("id, venue_id, start_time, end_time, hourly_rate, break_is_paid, positions_needed, stripe_payment_intent_id, locked_platform_fee_pence, platform_fee_pence, captured_amount_pence, capture_status, capture_idempotency_key, applied_credit_pence")
        .eq("id", data.shift_id)
        .maybeSingle();
      if (error || !shift) throw new Error("Engagement not found.");

      const { data: isFinance } = await supabase.rpc("has_admin_capability", { _user_id: userId, _cap: "finance" });
      const isOwner = shift.venue_id === userId;
      if (!isOwner && !isFinance) throw new Error("Only the host venue or a finance admin may retry capture.");

      if (!["pending", "failed"].includes(String(shift.capture_status ?? ""))) {
        throw new Error(`Capture is not retryable from status: ${shift.capture_status ?? "none"}.`);
      }
      if (!shift.stripe_payment_intent_id) throw new Error("No PaymentIntent attached to this shift.");

      // Count actual workers — same source-of-truth as confirmShiftCompletion.
      const { data: bookings } = await supabase
        .from("bookings")
        .select("id")
        .eq("shift_id", shift.id)
        .eq("status", "accepted")
        .not("completed_at", "is", null);
      const actualWorkers = (bookings ?? []).length;
      const requestedWorkers = (shift.positions_needed as number) ?? actualWorkers;

      // Bounds validation: actual must never exceed requested.
      if (actualWorkers > requestedWorkers) {
        throw new Error(`Internal inconsistency: actual_workers (${actualWorkers}) exceeds requested (${requestedWorkers}).`);
      }

      const hours = hoursBetween(shift.start_time as string, shift.end_time as string);
      const billableHours = effectiveBillableHours(hours, (shift as { break_is_paid?: boolean }).break_is_paid);
      const sh = shift as { locked_platform_fee_pence?: number | null; platform_fee_pence?: number | null; capture_idempotency_key?: string | null };
      const lockedFee = sh.locked_platform_fee_pence ?? sh.platform_fee_pence ?? PLATFORM_FEE_PENCE;
      const wagePerWorkerPence = Math.round(Number(shift.hourly_rate) * billableHours * 100);
      const maxAuthPence = (wagePerWorkerPence + lockedFee) * requestedWorkers;
      const finalCapturePence = (wagePerWorkerPence + lockedFee) * actualWorkers;

      const { randomUUID } = await import("crypto");
      const idemKey = sh.capture_idempotency_key ?? randomUUID();
      if (!sh.capture_idempotency_key) {
        await supabase.from("shifts").update({ capture_idempotency_key: idemKey }).eq("id", shift.id);
      }

      const env: StripeEnv = process.env.STRIPE_LIVE_API_KEY ? "live" : "sandbox";
      const stripe = createStripeClient(env);
      const pi = await stripe.paymentIntents.retrieve(shift.stripe_payment_intent_id as string);
      const nowIso = new Date().toISOString();

      let nextStatus: string;
      let nextAmount: number | null = null;

      if (pi.status === "succeeded") {
        nextAmount = pi.amount_received ?? finalCapturePence;
        nextStatus = nextAmount < maxAuthPence ? "partially_captured" : "captured";
      } else if (pi.status === "canceled") {
        nextAmount = 0;
        nextStatus = "released";
      } else if (pi.status === "requires_capture" && finalCapturePence > 0) {
        await stripe.paymentIntents.capture(
          shift.stripe_payment_intent_id as string,
          { amount_to_capture: finalCapturePence },
          { idempotencyKey: `shift_capture_${shift.id}_${idemKey}` },
        );
        nextAmount = finalCapturePence;
        nextStatus = finalCapturePence < maxAuthPence ? "partially_captured" : "captured";
      } else if (pi.status === "requires_capture" && finalCapturePence === 0) {
        await stripe.paymentIntents.cancel(shift.stripe_payment_intent_id as string);
        nextAmount = 0;
        nextStatus = "released";
      } else {
        throw new Error(`PaymentIntent in unexpected state for retry: ${pi.status}`);
      }

      await supabase.from("shifts").update({
        captured_amount_pence: nextAmount,
        capture_status: nextStatus,
        last_captured_at: nowIso,
        capture_error: null,
      }).eq("id", shift.id);

      return { ok: true, capture_status: nextStatus, captured_amount_pence: nextAmount };
    } catch (err) {
      const msg = err instanceof Error ? err.message : getStripeErrorMessage(err);
      // Persist the latest failure reason so admins can see why it didn't recover.
      try {
        await context.supabase.from("shifts")
          .update({ capture_status: "failed", capture_error: msg })
          .eq("id", data.shift_id);
      } catch { /* best-effort */ }
      return { error: msg };
    }
  });

/**
 * Admin-only: load the payment-operations view of a single shift, including
 * the PaymentIntent ID and the persisted capture_idempotency_key for
 * copy-to-clipboard support and Stripe dashboard cross-reference.
 */
export const getAdminShiftPaymentDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ shift_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: isFinance } = await context.supabase.rpc("has_admin_capability", { _user_id: context.userId, _cap: "finance" });
    if (!isFinance) throw new Error("Finance admin only.");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: shift, error } = await supabaseAdmin
      .from("shifts")
      .select("id, venue_id, start_time, end_time, status, payment_status, total_cost, hourly_rate, positions_needed, stripe_payment_intent_id, stripe_checkout_session_id, capture_status, capture_idempotency_key, captured_amount_pence, last_captured_at, capture_error, locked_platform_fee_pence")
      .eq("id", data.shift_id)
      .maybeSingle();
    if (error || !shift) throw new Error("Shift not found.");
    const { data: bookings } = await supabaseAdmin
      .from("bookings")
      .select("id, bartender_id, status, completed_at, stripe_payment_intent_id")
      .eq("shift_id", data.shift_id);
    return { shift, bookings: bookings ?? [] };
  });
